import axios from 'axios';
import { reactive } from 'vue';
import { objectToFormData } from '/src/utils/formData.js';
import { useLogger } from '/src/composables/useLogger.js';

function extractPath (url) {
	try {
		const urlObj = new URL(url);
		return urlObj.pathname;
	} catch {
		return url;
	}
}

// api logging interceptors
axios.interceptors.request.use(
	config => {
		config.metadata = { startTime: Date.now() };
		return config;
	},
	error => Promise.reject(error),
);

axios.interceptors.response.use(
	response => {
		if (response.config.metadata?.startTime) {
			const duration = Date.now() - response.config.metadata.startTime;
			const { addApiLog } = useLogger();
			addApiLog(
				extractPath(response.config.url),
				response.config.method.toUpperCase(),
				duration,
				'success',
				response.status,
			);
		}
		return response;
	},
	error => {
		if (error.config?.metadata?.startTime) {
			const duration = Date.now() - error.config.metadata.startTime;
			const { addApiLog } = useLogger();
			addApiLog(
				extractPath(error.config.url),
				error.config.method.toUpperCase(),
				duration,
				'error',
				error.response?.status ?? error.code ?? null,
			);
		}
		return Promise.reject(error);
	},
);

// cancel controller
const controller = new AbortController();

// api
const api = reactive({
	processing: false,
	submit (method, url, options = {}) {
		const request = {
			...options,
			method,
			url,
			onStart: () => {
				this.processing = true;

				if (options.onStart) {
					return options.onStart();
				}
			},
			onSuccess: (data) => {
				this.processing = false;

				if (options.onSuccess) {
					return options.onSuccess(data);
				}
			},
			onError: (errors, raw) => {
				this.processing = false;

				if (options.onError) {
					return options.onError(errors, raw);
				}
			},
			onFinish: () => {
				this.processing = false;

				if (options.onFinish) {
					return options.onFinish();
				}
			},
		};

		return fetch(request);
	},
	get (url, options = {}) {
		return this.submit('GET', url, { ...options });
	},
	post (url, data, options = {}) {
		return this.submit('POST', url, { ...options, data });
	},
	put (url, data, options = {}) {
		return this.submit('PUT', url, { ...options, data });
	},
	patch (url, data, options = {}) {
		return this.submit('PATCH', url, { ...options, data });
	},
	delete (url, options = {}) {
		return this.submit('DELETE', url, { ...options });
	},
	cancel () {
		controller.abort();
	},
});

async function fetch (request = {}) {
	try {
		request.onStart();

		if (request.forceFormData) {
			request.data = objectToFormData(request.data);
			request.headers['Content-Type'] = 'multipart/form-data';
		}

		// if ((hasFiles(request.data) || request.forceFormData) && !(request.data instanceof FormData)) {
		//     request.data = objectToFormData(request.data)
		// }

		const response = await axios({
			...request,
			signal: request.signal ?? controller.signal,
			params: request.query || {},
		});

		const data = request.withMeta
			? response.data
			: response.data?.data
		;

		request.onSuccess(data);

		return data;
	} catch (error) {
		const errors = error?.response?.data?.errors ??
			error?.response?.data?.error ??
			error
		;

		request.onError(errors, error);
		throw errors;
	} finally {
		request.onFinish();
	}
}

export default api;
